import type {
  Cell,
  Header,
  Row,
  Table as ReactTable,
} from '@tanstack/react-table';
import {flexRender} from '@tanstack/react-table';
import {ArrowDown, ArrowUp, ArrowUpDown} from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {cn} from '@/utils/ui';

type ClassNameValue<TContext> =
  | string
  | ((context: TContext) => string | undefined);

export type TableViewProps<TData> = {
  table: ReactTable<TData>;
  className?: string;
  tableClassName?: string;
  headerCellClassName?: ClassNameValue<Header<TData, unknown>>;
  rowClassName?: ClassNameValue<Row<TData>>;
  cellClassName?: ClassNameValue<Cell<TData, unknown>>;
  onRowClick?: (row: Row<TData>) => void;
  showSortingControls?: boolean;
};

function resolveClassName<TContext>(
  className: ClassNameValue<TContext> | undefined,
  context: TContext
) {
  return typeof className === 'function' ? className(context) : className;
}

function TableView<TData>({
  table,
  className,
  tableClassName,
  headerCellClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  showSortingControls = true,
}: TableViewProps<TData>) {
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <Table className={tableClassName}>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const canSort =
                  showSortingControls && header.column.getCanSort();
                const isSorted = header.column.getIsSorted();

                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={
                      canSort
                        ? () => header.column.toggleSorting(undefined, true)
                        : undefined
                    }
                    className={cn(
                      {'cursor-pointer select-none': canSort},
                      resolveClassName(headerCellClassName, header)
                    )}
                  >
                    {!header.isPlaceholder && (
                      <div className="flex items-center gap-2">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {canSort && (
                          <span className="text-muted-foreground">
                            {isSorted === 'asc' ? (
                              <ArrowUp size={14} />
                            ) : isSorted === 'desc' ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUpDown size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.map(row => (
            <TableRow
              key={row.id}
              onClick={() => onRowClick?.(row)}
              tabIndex={onRowClick ? 0 : undefined}
              className={cn(
                {'cursor-pointer': !!onRowClick},
                resolveClassName(rowClassName, row)
              )}
            >
              {row.getVisibleCells().map(cell => (
                <TableCell
                  key={cell.id}
                  className={cn(resolveClassName(cellClassName, cell))}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default TableView;
